import { IconActionDraw24 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, useCallback } from 'react';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { setGeo } from '@/packages/JsonQuery';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { usePolygonsSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { filtersSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/dumb';
import { useMapConfigurationDrawModeSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

import { DrawButton } from './DrawButton';
import { trackAreaSelect } from './tracking';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setDrawMode: reduxActions.mapConfiguration.setDrawMode,
  setFilters: reduxActions.jsonQuery.setFilters,
}));

export const DrawButtonContainer: FC = () => {
  const isDrawingMode = useMapConfigurationDrawModeSelector();
  const polygons = usePolygonsSelector();
  const hasPolygons = polygons.length > 0;
  const isActive = !hasPolygons && isDrawingMode;
  const actions = useBoundActions();
  const reduxStore = useReduxState();

  const handleActivateClick = useCallback(() => {
    if (!isDrawingMode) {
      actions.setDrawMode(true);
      trackAreaSelect('start');
    } else {
      actions.setDrawMode(false);
      trackAreaSelect('close');
    }
  }, [actions, isDrawingMode]);

  const handleCancelClick = useCallback(() => {
    actions.setDrawMode(false);

    const filters = filtersSelector(reduxStore.getState());

    const geoValues = filters.geo?.value.filter(({ type }) => type !== 'polygon') || [];

    const nextFilters = setGeo()(filters)(geoValues);

    actions.setFilters(nextFilters);

    trackAreaSelect('close');
  }, [actions, reduxStore]);

  return (
    <DrawButton
      hasPolygons={hasPolygons}
      icon={<IconActionDraw24 color={isActive ? 'white_100' : 'black_100'} />}
      isActive={isActive}
      onActivateClick={handleActivateClick}
      onCancelClick={handleCancelClick}
    />
  );
};
